import { isAxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import {
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
} from '@chakra-ui/react'

import { GetLinkedInProfileResponse } from '@pkg/types/api'
import { CandidatePictureAlternative } from '@pkg/types/interview'

import api from '@/api'
import {
  useCandidate,
  useCreateCandidate,
  useUpdateCandidate,
  useUploadCandidatePicture,
} from '@/react-query/candidate'

interface InputLinkedinCandidateForm {
  linkedinUrl: string
}

const LinkedinPublicIdentifierRegex = /([a-zA-Z0-9-]+)\/?\s*$/

export function InputLinkedinCandidate() {
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    register,
  } = useForm<InputLinkedinCandidateForm>({
    defaultValues: {
      linkedinUrl: '',
    },
  })

  const createCandidate = useCreateCandidate()
  const updateCandidate = useUpdateCandidate()
  const candidate = useCandidate().data
  const uploadCandidatePicture = useUploadCandidatePicture()

  const createCandidateFromLinkedInUrl = async ({
    linkedinUrl,
  }: InputLinkedinCandidateForm) => {
    try {
      const linkedinProfile = await fetchLinkedInData(linkedinUrl)
      if (!linkedinProfile) {
        return
      }

      let pictureAlternatives: CandidatePictureAlternative[] = []
      const profileImageUrl =
        linkedinProfile.profile_photo ||
        linkedinProfile.background_cover_image_url

      console.log('📸 Profile image URLs:', {
        profile_photo: linkedinProfile.profile_photo,
        background_cover_image_url: linkedinProfile.background_cover_image_url,
        selected: profileImageUrl,
      })

      // Build alternatives array with LinkedIn profile picture first if it exists
      if (profileImageUrl) {
        pictureAlternatives = [
          {
            url: profileImageUrl,
            source: 'linkedin',
          },
        ]
      }

      // Add existing candidate picture alternatives (if updating existing candidate)
      if (candidate?.PictureAlternatives) {
        // Filter out any existing LinkedIn pictures to avoid duplicates
        const nonLinkedInAlternatives = candidate.PictureAlternatives.filter(
          (alt) => alt.source !== 'linkedin',
        )
        pictureAlternatives = [
          ...pictureAlternatives,
          ...nonLinkedInAlternatives,
        ]
      }

      let finalProfilePictureURL: string | undefined

      // Priority order for main profile picture:
      // 1. Successfully uploaded LinkedIn image
      // 2. Original LinkedIn image URL
      // 3. Existing candidate picture
      if (profileImageUrl) {
        console.log('⬆️ Attempting to upload image from URL:', profileImageUrl)
        try {
          const uploadResult = await uploadPictureFromURL(profileImageUrl)
          finalProfilePictureURL = uploadResult?.profilePictureURL || undefined
          console.log('✅ Image upload result:', {
            uploadResult,
            finalProfilePictureURL,
          })
        } catch (error) {
          console.error('❌ Image upload failed:', error)
        }

        // If upload failed, use the original LinkedIn URL as fallback
        if (!finalProfilePictureURL) {
          finalProfilePictureURL = profileImageUrl
        }
      }

      if (candidate?.id) {
        await updateCandidate.mutateAsync({
          candidate: {
            id: candidate.id,
            fullName: candidate.fullName || linkedinProfile.fullName,
            profilePictureURL:
              finalProfilePictureURL || candidate.profilePictureURL,
            linkedInURL: `https://www.linkedin.com/in/${linkedinProfile.public_identifier}/`,
            cvText: linkedinToTextCV(linkedinProfile),
          },
        })
      } else {
        await createCandidate.mutateAsync({
          candidate: {
            fullName: linkedinProfile.fullName,
            profilePictureURL: finalProfilePictureURL,
            PictureAlternatives: pictureAlternatives,
            linkedInURL: `https://www.linkedin.com/in/${linkedinProfile.public_identifier}/`,
            cvText: linkedinToTextCV(linkedinProfile),
          },
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchLinkedInData = async (linkedinUrl: string) => {
    const publicIdentifier = getPublicIdentifierFromLinkedinUrl(linkedinUrl)

    try {
      return (await api.getLinkedInProfile(publicIdentifier)).data
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        setError('linkedinUrl', {
          message:
            'Unable to get LinkedIn profile from URL. Please check if the profile is public.',
        })
        return
      }
      console.error("Couldn't get LinkedIn profile", err)
      setError('linkedinUrl', {
        message:
          'Unable to get LinkedIn profile. Please try again or check if the profile is public.',
      })
      return
    }
  }

  const getPublicIdentifierFromLinkedinUrl = (url: string) => {
    // try to format URL to the format of https://www.linkedin.com/in/<public-identifier>
    url = url.trim()
    if (url.endsWith('/')) {
      url = url.slice(0, -1)
    }
    const parts = url.split('/')
    return parts[parts.length - 1]
  }

  const uploadPictureFromURL = async (url: string) => {
    try {
      const request = new FormData()
      // we need to append actual file, not the url
      const response = await fetch(url)
      const blob = await response.blob()
      request.append('files', blob)

      return await uploadCandidatePicture.mutateAsync(request)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <form onSubmit={handleSubmit(createCandidateFromLinkedInUrl)}>
      <FormControl isInvalid={Boolean(errors.linkedinUrl?.message)}>
        <InputGroup
          mt={6}
          display={'flex'}
          flexDirection={'row'}
          background={'white'}
          rounded={'full'}
        >
          <Input
            placeholder="LinkedIn URL"
            variant={'brand-solid-white'}
            rounded={'full'}
            id={'linkedinUrl'}
            {...register('linkedinUrl', {
              required: true,
              pattern: {
                value: LinkedinPublicIdentifierRegex,
                message:
                  'LinkedIn Profile URL looks incorrect. Please make sure to insert the full URL',
              },
            })}
          />
          <Button
            rounded={'1.25rem'}
            isLoading={isSubmitting}
            type="submit"
            display="flex"
            fontWeight={'bold'}
            variant={'brand-solid-white'}
            px={5}
            flexShrink={0}
          >
            Get
          </Button>
        </InputGroup>
        <FormErrorMessage>
          {errors.linkedinUrl && errors.linkedinUrl.message}
        </FormErrorMessage>
      </FormControl>
    </form>
  )
}
const linkedinToTextCV = (linkedinProfile: GetLinkedInProfileResponse) => {
  let result = ''

  // Add name, headline, and summary
  if (linkedinProfile.fullName) {
    result += `${linkedinProfile.fullName}\n`
  }
  if (linkedinProfile.headline) {
    result += `${linkedinProfile.headline}\n\n`
  }
  if (linkedinProfile.about) {
    result += `About: ${linkedinProfile.about}\n\n`
  }

  if (linkedinProfile.location) {
    result += `Location: ${linkedinProfile.location}\n\n`
  }

  // Add Experiences
  if (linkedinProfile.experience && linkedinProfile.experience.length > 0) {
    result += 'Experience:\n'
    linkedinProfile.experience.forEach((exp) => {
      result += `\n- ${exp.position} at ${exp.company_name}`
      if (exp.location) {
        result += ` (${exp.location})`
      }
      if (exp.starts_at) {
        result += `\n  ${exp.starts_at}`
      }
      if (exp.ends_at && exp.ends_at !== 'Present') {
        result += ` to ${exp.ends_at}`
      } else {
        result += ' to Present'
      }
      result += `\n  ${exp.summary || ''}\n`
    })
    result += '\n'
  }

  // Add Education
  if (linkedinProfile.education && linkedinProfile.education.length > 0) {
    result += 'Education:\n'
    linkedinProfile.education.forEach((edu) => {
      result += `\n- ${edu.college_degree} ${edu.college_degree_field ? `in ${edu.college_degree_field}` : ''}`
      result += `\n  ${edu.college_name}`
      if (edu.college_duration) {
        result += `\n  ${edu.college_duration}`
      }
      if (edu.college_activity) {
        result += `\n  ${edu.college_activity}`
      }
      result += '\n'
    })
    result += '\n'
  }

  // Add Languages
  if (linkedinProfile.languages && linkedinProfile.languages.length > 0) {
    result += 'Languages:\n'
    const languageList = linkedinProfile.languages
      .map((lang) => `${lang.name} (${lang.level})`)
      .join(', ')
    result += languageList + '\n\n'
  }

  // Add Courses
  if (linkedinProfile.courses && linkedinProfile.courses.length > 0) {
    result += 'Courses:\n'
    linkedinProfile.courses.forEach((course) => {
      result += `\n- ${course.name}`
      result += '\n'
    })
    result += '\n'
  }

  // Add Publications
  if (linkedinProfile.publications && linkedinProfile.publications.length > 0) {
    result += 'Publications:\n'
    linkedinProfile.publications.forEach((pub) => {
      result += `\n- ${pub.name || pub.title}`
      if (pub.url) {
        result += `\n  ${pub.url}`
      }
      result += '\n'
    })
    result += '\n'
  }

  // Add Volunteer Work
  if (linkedinProfile.volunteering && linkedinProfile.volunteering.length > 0) {
    result += 'Volunteer Work:\n'
    linkedinProfile.volunteering.forEach((vol) => {
      result += `\n- ${vol.company_position} at ${vol.company_name}`
      if (vol.starts_at || vol.ends_at) {
        result += `\n  ${vol.starts_at} to ${vol.ends_at}`
      }
      result += '\n'
    })
    result += '\n'
  }

  if (linkedinProfile.connections) {
    result += `Connections: ${linkedinProfile.connections}\n\n`
  }
  if (linkedinProfile.followers) {
    result += `Followers: ${linkedinProfile.followers}\n`
  }

  return result.trim()
}
